import {
  ActiveCallsResult,
  AgentStatusInfo,
  AudioStats,
  AudioStatsElement,
  CallInfo,
  CallResult,
  Contact,
  GenericResult,
  HangupResult,
  HoldToggleResult,
  LogoutResult,
  MuteToggleResult,
  ParticipantResult,
  PhoneCall,
  RecordingToggleResult,
  SharedCapabilitiesResult,
  StatsInfo,
  SuperviseCallResult,
  VoiceCapabilitiesResult,
} from '@salesforce/scv-connector-base';
import { Saturated } from '../Types/Saturated';
import { AmcCall } from './AmcCall';

/*
    Utility functions for resaturating Salesforce structures
    that may have been stripped of prototype or class info
 */

export function saturateContact(data: Contact): Saturated<Contact> {
  return {
    saturated: new Contact(data)
  };
}

export function saturateCallInfo(data: CallInfo): Saturated<CallInfo> {
  data.callStateTimestamp = new Date(data.callStateTimestamp);
  return {
    saturated: new CallInfo(data)
  };
}

export function saturateAmcCall(data: AmcCall): Saturated<AmcCall> {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (data.contact) {
    data.contact = saturateContact(data.contact).saturated;
  }

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (data.callInfo) {
    data.callInfo = saturateCallInfo(data.callInfo).saturated;
  }

  return {
    saturated: new AmcCall(data)
  };
}

export function saturatePhoneCall(data: PhoneCall): Saturated<PhoneCall> {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (data.contact) {
    data.contact = saturateContact(data.contact).saturated;
  }
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (data.callInfo) {
    data.callInfo = saturateCallInfo(data.callInfo).saturated;
  }

  return {
    saturated: new PhoneCall(data)
  };
}

export function saturateCallResult(data: CallResult): Saturated<CallResult> {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (data.call) {
    data.call = saturatePhoneCall(data.call).saturated;
  }

  return {
    saturated: new CallResult(data),
  };
}

export function saturateActiveCallsResult(data: ActiveCallsResult): Saturated<ActiveCallsResult> {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (data.activeCalls) {
    for (let i = 0; i < data.activeCalls.length; i++) {
      data.activeCalls[i] = saturatePhoneCall(data.activeCalls[i]).saturated;
    }
  }

  return {
    saturated: new ActiveCallsResult({activeCalls: data.activeCalls})
  };
}

export function saturateParticipantResult(
  data: ParticipantResult
): Saturated<ParticipantResult> {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (data.callInfo) {
    data.callInfo = saturateCallInfo(data.callInfo).saturated;
  }

  return {
    saturated: new ParticipantResult(data),
  };
}

export function saturateHoldToggleResult(data: HoldToggleResult): Saturated<HoldToggleResult> {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  for (let i = 0; data.calls && i < data.calls.length; i++) {
    data.calls[i] = saturatePhoneCall(data.calls[i]).saturated;
  }

  return {
    saturated: new HoldToggleResult(data)
  };
}

export function saturateSuperviseCallResult(data: SuperviseCallResult): Saturated<SuperviseCallResult> {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (data.call) {
    data.call = saturatePhoneCall(data.call).saturated;
  }

  return {
    saturated: new SuperviseCallResult(data)
  };
}

export function saturateHangupResult(data: HangupResult): Saturated<HangupResult> {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  for (let i = 0; data.calls && i < data.calls.length; i++) {
    data.calls[i] = saturatePhoneCall(data.calls[i]).saturated;
  }

  return {
    saturated: new HangupResult(data)
  };
}

export function saturateMuteToggleResult(data: MuteToggleResult): Saturated<MuteToggleResult> {
  return {
    saturated: new MuteToggleResult(data)
  };
}

export function saturateRecordingToggleResult(data: RecordingToggleResult): Saturated<RecordingToggleResult> {
  return {
    saturated: new RecordingToggleResult(data)
  };
}

export function saturateStatsInfo(data: StatsInfo): Saturated<StatsInfo> {
  return {
    saturated: new StatsInfo(data)
  };
}

export function saturateAudioStatsElement(data: AudioStatsElement): Saturated<AudioStatsElement> {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (data.inputChannelStats) {
    data.inputChannelStats = saturateStatsInfo(data.inputChannelStats).saturated;
  }
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (data.outputChannelStats) {
    data.outputChannelStats = saturateStatsInfo(data.outputChannelStats).saturated;
  }

  return {
    saturated: new AudioStatsElement(data)
  };
}

export function saturateAudioStats(data: AudioStats): Saturated<AudioStats> {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  for (let i = 0; data.stats && i < data.stats.length; i++) {
    data.stats[i] = saturateAudioStatsElement(data.stats[i]).saturated;
  }

  return {
    saturated: new AudioStats(data)
  };
}

export function saturateAgentStatusInfo(data: AgentStatusInfo): Saturated<AgentStatusInfo> {
  return {
    saturated: new AgentStatusInfo(data)
  };
}

export function saturateGenericResult(data: GenericResult): Saturated<GenericResult> {
  return {
    saturated: new GenericResult(data)
  };
}

export function saturateLogoutResult(data: LogoutResult): Saturated<LogoutResult> {
  return {
    saturated: new LogoutResult(data)
  };
}

export function saturateVoiceCapabilitiesResult(data: VoiceCapabilitiesResult): Saturated<VoiceCapabilitiesResult> {
  return {
    saturated: new VoiceCapabilitiesResult(data)
  };
}

export function saturateSharedCapabilitiesResult(data: SharedCapabilitiesResult): Saturated<SharedCapabilitiesResult> {
  return {
    saturated: new SharedCapabilitiesResult(data)
  };
}
